/**
 * Order references that tie an app sale back to a marketplace report row.
 *
 * Mirrors `services/api/src/lib/depop.ts` — keep the two in step. The Depop CSV
 * has no order-id column, so buyer + sale day is the key. The API stamps a full
 * `depop:<buyer>:<YYYYMMDD>-<HHMM>/<sku>` when it reconciles a report; a sale
 * recorded by hand only knows the buyer and the day, and that shorter form is a
 * clean prefix of the full one, so it still ties out.
 */

/** Platforms whose reports we can tie back to, and what to call the buyer field. */
export const BUYER_LABEL: Record<string, string> = {
  depop: "Depop username",
  vinted: "Vinted username",
  poshmark: "Poshmark username",
  mercari: "Mercari username",
  ebay: "eBay username",
};

/** `depop:obiwh_948:20260729` from a buyer handle and a YYYY-MM-DD date. */
export function orderKey(platform: string, buyer: string, isoDate: string): string | null {
  const b = buyer.trim().toLowerCase();
  if (!b || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null;
  return `${platform}:${b}:${isoDate.replace(/-/g, "")}`;
}

/** Per-item reference. `external_order_id` is unique per platform. */
export function saleRef(key: string, sku: string): string {
  return `${key}/${sku}`;
}

/** Human-readable buyer + date pulled back out of a stored reference. */
export function describeRef(ref: string | null): string | null {
  if (!ref) return null;
  const m = ref.match(/^([a-z]+):([^:]*):(\d{4})(\d{2})(\d{2})(?:-(\d{2})(\d{2}))?(?:\/(.+))?$/);
  if (!m) return ref;
  const [, , buyer, y, mo, d, hh, mm] = m;
  const time = hh && mm ? ` ${hh}:${mm}` : "";
  return `${buyer} · ${y}-${mo}-${d}${time}`;
}
